import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Home, ShoppingCart, Utensils, ChefHat, Package, Users, Settings,
    CreditCard, BarChart3, FileText, Calendar, Clock, Bell, 
    TrendingUp, Layers, Store, Shield, ArrowRight, ArrowDown,
    CheckCircle, AlertTriangle, X, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Menu structure data
const menuStructure = {
    accueil: {
        title: "ACCUEIL",
        icon: Home,
        color: "bg-green-500",
        description: "Point d'entrée principal",
        items: [
            { name: "Tableau de bord", path: "/dashboard", desc: "Vue personnelle, présence, objectifs" },
            { name: "Dashboard Admin", path: "/admin-dashboard", desc: "KPIs temps réel, graphiques, alertes", role: "Manager" },
            { name: "Supervision Live", path: "/live-monitoring", desc: "Suivi en direct de l'activité", role: "Manager" }
        ],
        flows: ["caisse", "stock"]
    },
    caisse: {
        title: "CAISSE & SALLE",
        icon: CreditCard,
        color: "bg-blue-500",
        description: "Gestion des ventes et de la salle",
        items: [
            { name: "Caisse (POS)", path: "/pos", desc: "Prise de commandes, encaissement" },
            { name: "Tables", path: "/tables", desc: "État des tables, transferts, fusions" },
            { name: "Menu & Tarifs", path: "/menus", desc: "Cartes, formules, prix" },
            { name: "Formules", path: "/formulas", desc: "Menus du jour, combos" },
            { name: "Réservations", path: "/reservations", desc: "Gestion des réservations" },
            { name: "Ventes du jour", path: "/daily-sales", desc: "Historique des ventes" },
            { name: "Fidélité", path: "/loyalty", desc: "Programme fidélité clients" },
            { name: "Supervision Caisses", path: "/cash-supervision", desc: "Ouverture/clôture caisses", role: "Manager" }
        ],
        flows: ["cuisine", "stock", "rapports"]
    },
    cuisine: {
        title: "CUISINE",
        icon: ChefHat,
        color: "bg-orange-500",
        description: "Gestion de la production",
        items: [
            { name: "Écran Cuisine", path: "/kitchen-dashboard", desc: "Commandes à préparer" },
            { name: "Préparations", path: "/preparations", desc: "Recettes, fiches techniques" }
        ],
        flows: ["caisse"]
    },
    stock: {
        title: "STOCK & ACHATS",
        icon: Package,
        color: "bg-purple-500",
        description: "Gestion des approvisionnements",
        items: [
            { name: "Gestion Stock", path: "/products", desc: "Produits, quantités, valeurs" },
            { name: "Mouvements", path: "/stock-movements", desc: "Entrées, sorties, ajustements" },
            { name: "Achats", path: "/purchases", desc: "Commandes fournisseurs" },
            { name: "Fournisseurs", path: "/suppliers", desc: "Carnet d'adresses fournisseurs" },
            { name: "Inventaire", path: "/inventory", desc: "Comptage et valorisation" },
            { name: "Contrôle Stock", path: "/stock-control", desc: "Alertes, péremptions" }
        ],
        flows: ["rapports", "cuisine"]
    },
    rh: {
        title: "RH & ADMIN",
        icon: Users,
        color: "bg-teal-500",
        description: "Gestion du personnel",
        items: [
            { name: "Employés", path: "/staff", desc: "Liste du personnel" },
            { name: "Planning", path: "/planning", desc: "Horaires de travail" },
            { name: "Heures Sup", path: "/overtime", desc: "Heures supplémentaires" },
            { name: "Présence", path: "/attendance", desc: "Pointages, absences" },
            { name: "Historique", path: "/attendance-history", desc: "Historique présences" },
            { name: "Paie", path: "/payroll", desc: "Calcul des salaires" },
            { name: "Alertes", path: "/alerts", desc: "Notifications RH" }
        ],
        flows: ["rapports"]
    },
    admin: {
        title: "ADMINISTRATION",
        icon: Settings,
        color: "bg-red-500",
        description: "Configuration système",
        items: [
            { name: "Établissements", path: "/establishments", desc: "Multi-sites" },
            { name: "Utilisateurs", path: "/users", desc: "Comptes et droits" },
            { name: "Journal d'audit", path: "/audit-log", desc: "Traçabilité actions" },
            { name: "Historique urgences", path: "/emergencies", desc: "Alertes passées" },
            { name: "Assistant IA", path: "/ai-assistant", desc: "Aide intelligente" },
            { name: "Paramètres", path: "/settings", desc: "Configuration générale" },
            { name: "Paramètres Supervision", path: "/supervision-settings", desc: "Seuils d'alertes", role: "Admin" }
        ],
        flows: []
    },
    rapports: {
        title: "RAPPORTS",
        icon: BarChart3,
        color: "bg-indigo-500",
        description: "Analyses et exports",
        items: [
            { name: "Rapport Journalier", path: "/reports", desc: "Synthèse quotidienne" },
            { name: "Prédictions IA", path: "/ai-predictions", desc: "Analyses prédictives" }
        ],
        flows: []
    }
};

// Workflow connections
const workflows = [
    {
        title: "Flux de Commande",
        color: "border-green-500",
        steps: [
            { menu: "caisse", item: "Tables", action: "Client arrive" },
            { menu: "caisse", item: "Caisse (POS)", action: "Prise de commande" },
            { menu: "cuisine", item: "Écran Cuisine", action: "Préparation" },
            { menu: "caisse", item: "Caisse (POS)", action: "Service & Paiement" },
            { menu: "caisse", item: "Tables", action: "Libération table" }
        ]
    },
    {
        title: "Flux de Stock",
        color: "border-purple-500",
        steps: [
            { menu: "stock", item: "Contrôle Stock", action: "Alerte stock bas" },
            { menu: "stock", item: "Achats", action: "Commande fournisseur" },
            { menu: "stock", item: "Mouvements", action: "Réception marchandise" },
            { menu: "stock", item: "Gestion Stock", action: "Mise à jour stock" }
        ]
    },
    {
        title: "Flux de Caisse",
        color: "border-blue-500",
        steps: [
            { menu: "caisse", item: "Supervision Caisses", action: "Ouverture caisse" },
            { menu: "caisse", item: "Caisse (POS)", action: "Ventes de la journée" },
            { menu: "caisse", item: "Ventes du jour", action: "Consultation ventes" },
            { menu: "caisse", item: "Caisse (POS)", action: "Demande clôture" },
            { menu: "caisse", item: "Supervision Caisses", action: "Validation clôture" }
        ]
    },
    {
        title: "Flux RH Quotidien",
        color: "border-teal-500",
        steps: [
            { menu: "rh", item: "Planning", action: "Horaires prévus" },
            { menu: "rh", item: "Présence", action: "Pointage arrivée" },
            { menu: "accueil", item: "Tableau de bord", action: "Vue présence" },
            { menu: "rh", item: "Heures Sup", action: "Calcul heures sup" },
            { menu: "rh", item: "Paie", action: "Préparation paie" }
        ]
    }
];

const GuidePage = () => {
    const navigate = useNavigate();
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [selectedWorkflow, setSelectedWorkflow] = useState(0);

    const handleNavigate = (path) => {
        navigate(path);
    };

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-testid="guide-page">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                    Guide Interactif ALDRIN'S
                </h1>
                <p className="text-muted-foreground">
                    Cliquez sur un menu pour voir ses fonctionnalités et ses liens
                </p>
            </div>

            {/* Menu Structure - Visual Map */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                        <Layers className="w-5 h-5" />
                        Structure des Menus
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                        {Object.entries(menuStructure).map(([key, menu]) => {
                            const Icon = menu.icon;
                            const isSelected = selectedMenu === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setSelectedMenu(isSelected ? null : key)}
                                    className={`p-3 rounded-xl text-center transition-all ${menu.color} text-white
                                        ${isSelected ? 'ring-4 ring-offset-2 ring-black scale-105' : 'hover:scale-105 hover:shadow-lg'}
                                    `}
                                >
                                    <Icon className="w-6 h-6 mx-auto mb-1" />
                                    <p className="text-xs font-medium">{menu.title}</p>
                                </button>
                            );
                        })}
                    </div>

                    {/* Selected Menu Details */}
                    {selectedMenu && (
                        <div className="mt-6 p-4 bg-muted rounded-xl animate-in fade-in duration-300">
                            <div className="flex items-center gap-3 mb-4">
                                {React.createElement(menuStructure[selectedMenu].icon, {
                                    className: `w-8 h-8 p-1.5 rounded-lg text-white ${menuStructure[selectedMenu].color}`
                                })}
                                <div>
                                    <h3 className="font-bold text-lg">{menuStructure[selectedMenu].title}</h3>
                                    <p className="text-sm text-muted-foreground">{menuStructure[selectedMenu].description}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {menuStructure[selectedMenu].items.map((item, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handleNavigate(item.path)}
                                        className="flex items-start gap-2 p-3 bg-background rounded-lg hover:bg-accent transition-colors text-left group"
                                    >
                                        <ArrowRight className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-primary" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">{item.name}</span>
                                                {item.role && (
                                                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                                                        {item.role}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">{item.desc}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {menuStructure[selectedMenu].flows.length > 0 && (
                                <div className="mt-4 pt-4 border-t">
                                    <p className="text-xs text-muted-foreground mb-2">Connecté à :</p>
                                    <div className="flex flex-wrap gap-2">
                                        {menuStructure[selectedMenu].flows.map(flow => (
                                            <Badge 
                                                key={flow}
                                                className={`${menuStructure[flow].color} cursor-pointer`}
                                                onClick={() => setSelectedMenu(flow)}
                                            >
                                                {menuStructure[flow].title}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Workflows */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Flux de Travail
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Workflow Tabs */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {workflows.map((wf, i) => (
                            <Button
                                key={i}
                                variant={selectedWorkflow === i ? "default" : "outline"}
                                size="sm"
                                onClick={() => setSelectedWorkflow(i)}
                                className={selectedWorkflow === i ? "" : wf.color.replace("border-", "text-").replace("500", "600")}
                            >
                                {wf.title}
                            </Button>
                        ))}
                    </div>

                    {/* Selected Workflow Steps */}
                    <div className={`p-4 rounded-xl border-2 ${workflows[selectedWorkflow].color} bg-muted/30`}>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 overflow-x-auto pb-2">
                            {workflows[selectedWorkflow].steps.map((step, i) => (
                                <React.Fragment key={i}>
                                    <div 
                                        className={`flex-shrink-0 p-3 rounded-lg ${menuStructure[step.menu].color} text-white min-w-[140px] cursor-pointer hover:opacity-90`}
                                        onClick={() => setSelectedMenu(step.menu)}
                                    >
                                        <p className="text-[10px] opacity-80">{menuStructure[step.menu].title}</p>
                                        <p className="font-medium text-sm">{step.item}</p>
                                        <p className="text-xs mt-1 opacity-90">{step.action}</p>
                                    </div>
                                    {i < workflows[selectedWorkflow].steps.length - 1 && (
                                        <>
                                            <ArrowRight className="hidden sm:block w-6 h-6 mx-2 text-muted-foreground flex-shrink-0" />
                                            <ArrowDown className="sm:hidden w-6 h-6 my-1 mx-auto text-muted-foreground" />
                                        </>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Quick Reference */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Roles */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Shield className="w-5 h-5" />
                            Rôles & Accès
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {[
                            { role: "Admin", access: "Tout", color: "bg-red-500" },
                            { role: "Directeur", access: "Sauf paramètres système", color: "bg-orange-500" },
                            { role: "Dir. Adjoint", access: "Supervision, validation", color: "bg-amber-500" },
                            { role: "Chef Cuisine", access: "Cuisine, stock cuisine", color: "bg-yellow-500" },
                            { role: "Serveur", access: "Commandes, tables", color: "bg-green-500" },
                            { role: "Caissier", access: "Encaissement", color: "bg-blue-500" }
                        ].map((r, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${r.color}`}></span>
                                <span className="font-medium text-sm w-24">{r.role}</span>
                                <span className="text-xs text-muted-foreground">{r.access}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <CheckCircle className="w-5 h-5" />
                            Actions Rapides
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {[
                            { action: "Nouvelle vente", path: "/pos", desc: "Ouvrir la caisse" },
                            { action: "Voir les tables", path: "/tables", desc: "État de la salle" },
                            { action: "Écran cuisine", path: "/kitchen-dashboard", desc: "Commandes à préparer" },
                            { action: "Stock bas", path: "/stock-movements", desc: "Réapprovisionner" },
                            { action: "Rapport du jour", path: "/reports", desc: "Synthèse" }
                        ].map((a, i) => (
                            <Button
                                key={i}
                                variant="ghost"
                                className="w-full justify-start h-auto py-2"
                                onClick={() => handleNavigate(a.path)}
                            >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                <span className="font-medium">{a.action}</span>
                                <span className="text-xs text-muted-foreground ml-2">— {a.desc}</span>
                            </Button>
                        ))}
                    </CardContent>
                </Card>
            </div>

            {/* Download PDF Button */}
            <div className="text-center pt-4">
                <Button
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/guide-utilisation`, '_blank')}
                    className="bg-blue-600 hover:bg-blue-700"
                >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le guide complet (PDF)
                </Button>
            </div>
        </div>
    );
};

export default GuidePage;
